import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";

export class Projects extends SubAPI {
    recalculateFinancials(input: {
        project: number
    }) {
        return this.fetcher.post<{task: string}>("/api/project/recalculate-finances", input)
    }

    setPausePricing(input: {
        project: number;
        pause: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/project/set-pause-pricing", input);
    }

    award(input: Partial<Project>) {
        return this.fetcher.post<{task: string}>("/api/project/start-award", input)
    }

    awardStatus(input: {
        task: string;
    }) {
        return this.fetcher.get<{
            status: TaskStatus
            hasError: boolean;
            debug: any;
        }>("/api/project/award-status", input)
    }

    resetToQuoteMode(input: {
        project: number
    }) {
        return this.fetcher.post("/api/project/reset-to-quote-mode", input)
    }

    getProjectNumber(input: {
        project: number;
        alternative?: number;
    }) {
        return this.fetcher.get<string>("/api/project/project-number", input);
    }

    list(input: {
        search: string;
        offset: number;
        archived?: boolean;
        stage?: ProjectStage;
        company?: number;
        mode?: ProjectListMode
    }) {
        return this.fetcher.get<Paginated<Project>>("/api/projects/list", input)
    }

    createFromTestingTemplate(input: {
        name: string;
    }) {
        return this.fetcher.post<{task: string}>("/api/project/from-template", input);
    }

    upsert(input: {
        id?: number;
        name: string;
        quoteAlternative?: number;
        conversionAutoSequenceMode?: "default" | "per-floor";
        notifyDiscontinuedProducts?: boolean;
    }) {
        return this.fetcher.post<{id: number}>("/api/projects", input)
    }

    upsertTask(input: Partial<Project> & {copyFromAlternative?: number;}) {
        return this.fetcher.post<{task: string}>("/api/projects/with-task", input)
    }

    get(id: number, alternative?: number) {
        return this.fetcher.get<Project>("/api/project", {
            id: id,
            alternative: alternative,
        })
    }

    getShopDrawingMeta(input: {project: number}) {
        return this.fetcher.get<DrawingMeta>("/api/projects/shop-drawing-meta", input)
    }

    updateShopDrawingMeta(input: DrawingMeta) {
        return this.fetcher.post("/api/projects/shop-drawing-meta", input)
    }

    getChecklist(input: {project: number}) {
        return this.fetcher.get<Checklist>("/api/project-checklist", input);
    }

    saveChecklist(input: {
        project: number;
        checklist: Checklist;
    }) {
        return this.fetcher.post("/api/project-checklist", input);
    }

    listAltAddresses(input: {
        project: number;
        excludeContractor?: boolean;
    }) {
        return this.fetcher.get<AltAddress[]>("/api/project/alt-addresses", input)
    }

    upsertAltAddress(input: Partial<AltAddress>) {
        return this.fetcher.post<AltAddress>("/api/project/alt-address", input)
    }
}

export type AltAddress = {
    id: number;
    project: number;
    name: string;

    addressCity: string;
    addressPostal: string;
    addressProvince: string;
    addressStreet: string;

    updatedAt: DateString;
    updatedBy: number;
    archived: boolean;

    contractorId: number | null;
}

export type Checklist = {
    [key: string]: ChecklistItem;
}

export type ChecklistItem = {
    value: string;
    comment: string;
}

type DrawingMeta = {
    project: number;
    managerLockEnabled: boolean;
    changesetEnabled: boolean;
    changesetSessionId?: number;
}

export type DateString = string;

export type Project = {
    id: number;
    name: string;
    nameAlias: string | null;
    createdAt: DateString;
    updatedAt: DateString;
    archived: boolean;

    addressStreet:   string;
    addressCity:     string;
    addressProvince: string;
    addressPostal:   string;

    contractor?: number;
    contractorName?: string;

    gcContact?: number;
    gcContactName?: string;
    gcContactEmail?: string;
    gcContactPhone?: string;

    client?:             number;
    clientName?:         string;

    pmContact?:      number;
    pmContactName?:  string;
    pmContactPhone?: string;
    pmContactEmail?: string;

    estimators?: Estimator[];
    internalRep?:      number;
    internalRepName?:  string;
    internalRepTitle?: string;
    internalRepPhone?: string;

    projectManager?: number;
    pmTitle?: string;
    pmName?: string;
    pmPhone?: string;

    quoteAlternative?: number;
    quoteContractors: QuoteContractor[];
    quoteLockedOpenings?: number;
    quoteLockedHardware?: number;
    quoteLockedDiv10WrAccessories?: number;
    quoteLockedDiv10WrPartitions?: number;
    quoteLockedDiv10Lockers?: number;
    quoteLockedDiv10Mailboxes?: number;
    quoteLockedDiv10CornerGuards?: number;

    quotePausePricing?: boolean;

    unitSystem: UnitSystem;
    stage: ProjectStage;
    versionCode: string;
    projectNumber: number;

    contractCustomerPoNumber: string;
    awardedQuoteNumber: string;
    awardedDate: string | null;

    backgroundJobError: null | string;
    backgroundJobErrorDetail: null | string;

    startedWithSoftwareVersion: string;

    sovMainContractProof: number | null;
    sovMainContractProofName: string | null;

    sovLocked: boolean;

    notifyDiscontinuedProducts: boolean;
    needsRecalculateProducts: boolean;
    hasDynamicProductErrors: boolean;
    hasActiveChangeset: boolean;
}

export type Estimator = {
    id: number; // project_estimator id
    person: number;
    project: number;

    title: string;
    name: string;
    phone: string;

    archived: boolean;
}

export type QuoteContractor = {
    id: number;

    contractor: number;
    contractorName: string;

    contact?: number;
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;

    note: string;
    archived: boolean;
}

export type UnitSystem = "metric" | "imperial";
export type ProjectStage = "quote" | "in-progress"
export type ProjectListMode = "to-pack"

export type TaskStatus = {
    id: string;
    position: number;
    createdAt: DateString;
    startedAt: DateString | null;
    completedAt: DateString | null;
}
export function formatProjectNumber(projectNumber: number) {
    return "P" + projectNumber.toString().padStart(4, "0");
}

// customer-facing ref used on PDFs and file names
export function formatProjectPdfRef(projectNumber: number) {
    return "TK" + projectNumber.toString().padStart(4, "0");
}

// "P1001 - Name". falls back to the name alone when the number is missing (e.g. stocking rows)
export function projectLabel(input: {projectNumber?: number | null; name?: string | null}) {
    const name = input.name || "";
    if(!input.projectNumber) return name;
    return formatProjectNumber(input.projectNumber) + " - " + name;
}
